import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, CalendarDays, LogIn, Ticket, XCircle, CheckCircle2, TriangleAlert, MapPinCheck, Star, Award } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFF_STYLE = {
  soft: "text-[#7FB383] border-[#4E8752]/50 bg-[#4E8752]/10",
  trail: "text-ochre border-ochre/50 bg-ochre/10",
  extreme: "text-[#E86A50] border-[#D94235]/50 bg-[#D94235]/10",
};

const TIRES_ORDER = { street: 0, at: 1, mt: 2 };
const LIFT_ORDER = { stock: 0, lift2: 1, lift4: 2 };

function rigMeets(rig, req) {
  if (!req || !Object.keys(req).length) return true;
  const r = rig || {};
  if ((TIRES_ORDER[r.tires || "street"] ?? 0) < (TIRES_ORDER[req.min_tires || "street"] ?? 0)) return false;
  if (req.winch && !r.winch) return false;
  if ((LIFT_ORDER[r.lift || "stock"] ?? 0) < (LIFT_ORDER[req.min_lift || "stock"] ?? 0)) return false;
  return true;
}

export default function MemberPage() {
  const { t, lang } = useLanguage();
  const { user, setAuthOpen } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rig, setRig] = useState({ tires: "at", winch: false, lift: "stock" });
  const [badgeToken, setBadgeToken] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewedIds, setReviewedIds] = useState({});

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/members/me/bookings`, { withCredentials: true })
      .then((r) => setBookings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios
      .get(`${API}/members/me/badge`, { withCredentials: true })
      .then((r) => setBadgeToken(r.data.token))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user?.rig) setRig({ tires: "at", winch: false, lift: "stock", ...user.rig });
  }, [user]);

  const saveRig = async () => {
    try {
      await axios.put(`${API}/members/me/rig`, rig, { withCredentials: true });
      toast.success(t.member.rigSaved);
    } catch {
      toast.error(t.auth.genericError);
    }
  };

  if (user === null) {
    return <div className="min-h-screen bg-ink" data-testid="member-loading" />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-5" data-testid="member-denied">
        <div className="border border-line bg-surface p-10 max-w-md text-center">
          <LogIn size={36} className="text-blaze mx-auto mb-5" />
          <h1 className="font-display text-3xl font-extrabold uppercase text-bone">{t.member.denied}</h1>
          <div className="mt-7 flex justify-center gap-3">
            <button
              data-testid="member-login-btn"
              onClick={() => setAuthOpen(true)}
              className="font-telemetry text-xs uppercase tracking-[0.2em] bg-blaze text-ink px-6 py-3 hover:bg-sand transition-colors"
            >
              {t.member.loginCta}
            </button>
            <Link
              to="/"
              data-testid="member-back-home-link"
              className="font-telemetry text-xs uppercase tracking-[0.2em] border border-line text-sand px-6 py-3 hover:border-blaze hover:text-blaze transition-colors"
            >
              {t.member.back}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const downloadCert = async (ev) => {
    const canvas = document.createElement("canvas");
    canvas.width = 1200;
    canvas.height = 630;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0F0D0B";
    ctx.fillRect(0, 0, 1200, 630);
    ctx.strokeStyle = "#E85D1A";
    ctx.lineWidth = 3;
    ctx.strokeRect(24, 24, 1152, 582);
    ctx.strokeStyle = "#2E2620";
    ctx.lineWidth = 1;
    ctx.strokeRect(36, 36, 1128, 558);
    try {
      const logo = new Image();
      logo.src = "/logo.png";
      await new Promise((res, rej) => {
        logo.onload = res;
        logo.onerror = rej;
      });
      ctx.drawImage(logo, 60, 56, 250, 99);
    } catch {}
    ctx.textBaseline = "top";
    ctx.fillStyle = "#E85D1A";
    ctx.font = "700 22px 'JetBrains Mono', monospace";
    ctx.fillText(`${t.member.certTitle} — ${ev.coords || ""}`, 60, 210);
    ctx.fillStyle = "#F7F3EE";
    const title = ev.title[lang].toUpperCase();
    let size = 88;
    ctx.font = `900 ${size}px 'Barlow Condensed', sans-serif`;
    while (ctx.measureText(title).width > 1060 && size > 40) {
      size -= 6;
      ctx.font = `900 ${size}px 'Barlow Condensed', sans-serif`;
    }
    ctx.fillText(title, 60, 250);
    ctx.fillStyle = "#D19B4C";
    ctx.font = "600 30px 'Barlow Condensed', sans-serif";
    ctx.fillText(user.name.toUpperCase(), 60, 250 + size + 24);
    ctx.fillStyle = "#A89B8B";
    ctx.font = "500 19px 'JetBrains Mono', monospace";
    ctx.fillText(`${t.member.certDone} ${fmt(ev.date).toUpperCase()} — ${ev.location.toUpperCase()}`, 60, 250 + size + 78);
    ctx.fillStyle = "#E85D1A";
    ctx.fillRect(60, 545, 46, 5);
    ctx.fillStyle = "#756B62";
    ctx.font = "500 17px 'JetBrains Mono', monospace";
    ctx.fillText(t.footer.built.toUpperCase(), 122, 538);
    const a = document.createElement("a");
    a.download = `dust-republic-${ev.id}.png`;
    a.href = canvas.toDataURL("image/png");
    a.click();
    toast.success(t.member.certificate);
  };

  const sendReview = async (ev) => {
    try {
      await axios.post(`${API}/events/${ev.id}/review`, { rating, comment }, { withCredentials: true });
      setReviewedIds((p) => ({ ...p, [ev.id]: true }));
      setReviewFor(null);
      setComment("");
      setRating(5);
      toast.success(t.member.reviewSent);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.auth.genericError);
    }
  };

  const checkin = async (ev) => {
    try {
      await axios.post(`${API}/events/${ev.id}/checkin`, {}, { withCredentials: true });
      setBookings((prev) => prev.map((b) => (b.id === ev.id ? { ...b, checkin_at: new Date().toISOString() } : b)));
      toast.success(t.member.checkedIn);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.auth.genericError);
    }
  };

  const cancelBooking = async (ev) => {
    if (!window.confirm(t.member.cancelConfirm)) return;
    try {
      await axios.delete(`${API}/events/${ev.id}/book`, { withCredentials: true });
      setBookings((prev) => prev.filter((b) => b.id !== ev.id));
      toast.success(t.member.cancelled);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.auth.genericError);
    }
  };

  const fmt = (iso) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

  return (
    <div className="min-h-screen bg-ink" data-testid="member-area">
      <header className="border-b border-line bg-surface/60">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Dust Republic" className="h-10 w-auto" />
            <span className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze border border-blaze/40 px-2.5 py-1">
              {t.member.title}
            </span>
          </div>
          <Link
            to="/"
            data-testid="member-back-home-link"
            className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] text-sandmut hover:text-blaze transition-colors"
          >
            <ArrowLeft size={14} />
            {t.member.back}
          </Link>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-12 gap-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-4"
        >
          <div className="border border-line bg-surface p-7" data-testid="member-profile-card">
            <div className="w-16 h-16 bg-blaze/15 border border-blaze/50 flex items-center justify-center mb-5">
              <span className="font-display font-black text-3xl uppercase text-blaze">
                {user.name?.charAt(0)}
              </span>
            </div>
            <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-bone leading-tight">
              {user.name}
            </h1>
            <p className="mt-1 font-telemetry text-xs text-ochre">{user.email}</p>
            <div className="mt-6 flex flex-col gap-3 border-t border-line pt-5">
              <div className="flex justify-between font-telemetry text-[11px] uppercase tracking-[0.15em]">
                <span className="text-sandmut">{t.member.role}</span>
                <span className="text-sand">{user.role}</span>
              </div>
              <div className="flex justify-between font-telemetry text-[11px] uppercase tracking-[0.15em]">
                <span className="text-sandmut">{t.join.vehicle}</span>
                <span className="text-sand">{user.vehicle || "—"}</span>
              </div>
            </div>
            <div className="mt-6 border-t border-line pt-5" data-testid="member-rig-form">
              <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze mb-4">
                {t.member.rigTitle}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-telemetry text-[9px] uppercase tracking-[0.18em] text-sandmut mb-1.5">
                    {t.member.tires}
                  </label>
                  <select
                    data-testid="rig-select-tires"
                    value={rig.tires}
                    onChange={(e) => setRig((r) => ({ ...r, tires: e.target.value }))}
                    className="w-full bg-ink border border-line px-3 py-2.5 text-xs text-bone focus:outline-none focus:border-blaze cursor-pointer"
                  >
                    {Object.entries(t.member.tiresOpts).map(([k, v]) => (
                      <option key={k} value={k} className="bg-ink">{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-telemetry text-[9px] uppercase tracking-[0.18em] text-sandmut mb-1.5">
                    {t.member.lift}
                  </label>
                  <select
                    data-testid="rig-select-lift"
                    value={rig.lift}
                    onChange={(e) => setRig((r) => ({ ...r, lift: e.target.value }))}
                    className="w-full bg-ink border border-line px-3 py-2.5 text-xs text-bone focus:outline-none focus:border-blaze cursor-pointer"
                  >
                    {Object.entries(t.member.liftOpts).map(([k, v]) => (
                      <option key={k} value={k} className="bg-ink">{v}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                data-testid="rig-toggle-winch"
                onClick={() => setRig((r) => ({ ...r, winch: !r.winch }))}
                className={`mt-3 w-full font-telemetry text-[10px] uppercase tracking-[0.18em] border px-3 py-2.5 transition-colors ${
                  rig.winch
                    ? "border-blaze/60 text-blaze bg-blaze/10"
                    : "border-line text-sandmut hover:text-blaze hover:border-blaze"
                }`}
              >
                {t.member.winch}: {rig.winch ? "ON" : "OFF"}
              </button>
              <button
                data-testid="rig-save-btn"
                onClick={saveRig}
                className="mt-3 w-full font-telemetry text-[10px] uppercase tracking-[0.2em] bg-blaze text-ink px-3 py-3 hover:bg-sand transition-colors"
              >
                {t.member.saveRig}
              </button>
            </div>

            {badgeToken && (
              <div className="mt-6 border-t border-line pt-5" data-testid="member-qr-badge">
                <p className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-blaze mb-1.5">
                  {t.member.badgeTitle}
                </p>
                <p className="text-[11px] text-sandmut mb-4 leading-relaxed">{t.member.badgeSub}</p>
                <div className="bg-bone p-3 w-max border border-line">
                  <QRCodeCanvas
                    value={`${window.location.origin}/checkin/${badgeToken}`}
                    size={148}
                    bgColor="#F7F3EE"
                    fgColor="#0F0D0B"
                    data-testid="member-qr-canvas"
                  />
                </div>
                <p className="mt-3 font-telemetry text-[9px] uppercase tracking-[0.2em] text-faint break-all">
                  {badgeToken.slice(0, 12)}…
                </p>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-8"
        >
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-bone mb-6">
            {t.member.myEvents}
            <span className="text-blaze ml-3">{bookings.length}</span>
          </h2>

          {!loading && bookings.length === 0 && (
            <div className="border border-line bg-surface/60 p-10 text-center" data-testid="member-no-events">
              <Ticket size={30} className="text-sandmut mx-auto mb-4" />
              <p className="text-sm text-ash mb-6">{t.member.noEvents}</p>
              <Link
                to="/"
                data-testid="member-explore-events-btn"
                className="inline-block font-telemetry text-xs uppercase tracking-[0.2em] bg-blaze text-ink px-7 py-3.5 hover:bg-sand transition-colors"
              >
                {t.member.explore}
              </Link>
            </div>
          )}

          <div className="flex flex-col gap-4" data-testid="member-bookings-list">
            {bookings.map((ev) => (
              <div
                key={ev.id}
                data-testid={`member-booking-card-${ev.id}`}
                className="group border border-line bg-surface overflow-hidden flex flex-col sm:flex-row"
              >
                {ev.image && (
                  <div className="sm:w-52 h-36 sm:h-auto shrink-0 overflow-hidden">
                    <img
                      src={ev.image}
                      alt={ev.title[lang]}
                      loading="lazy"
                      className="w-full h-full object-cover warm-grade group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                )}
                <div className="flex-1 p-5 sm:p-6 flex flex-col justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <span className={`font-telemetry text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 ${DIFF_STYLE[ev.difficulty]}`}>
                        {t.events.difficulties[ev.difficulty]}
                      </span>
                      <span className="font-telemetry text-[10px] uppercase tracking-[0.15em] text-sandmut">
                        {t.member.bookedOn} {(ev.booked_at || "").slice(0, 10)}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-bone leading-tight">
                      {ev.title[lang]}
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1.5 font-telemetry text-[11px] uppercase tracking-[0.15em] text-sandmut">
                    <span className="flex items-center gap-1.5">
                      <CalendarDays size={12} className="text-ochre" />
                      {fmt(ev.date)} — {ev.days} {ev.days > 1 ? t.events.days : t.events.day}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <MapPin size={12} className="text-ochre" />
                      {ev.location}
                    </span>
                    <button
                      data-testid={`member-cancel-booking-${ev.id}`}
                      onClick={() => cancelBooking(ev)}
                      className="flex items-center gap-1.5 text-[#E86A50] hover:text-bone transition-colors"
                    >
                      <XCircle size={12} />
                      {t.member.cancel}
                    </button>
                    {!ev.checkin_at && new Date(ev.date) <= new Date() && (
                      <button
                        data-testid={`member-checkin-btn-${ev.id}`}
                        onClick={() => checkin(ev)}
                        className="flex items-center gap-1.5 text-blaze hover:text-sand transition-colors"
                      >
                        <MapPinCheck size={12} />
                        {t.member.checkin}
                      </button>
                    )}
                    {ev.checkin_at && (
                      <span className="flex items-center gap-1.5 text-[#7FB383]">
                        <CheckCircle2 size={12} />
                        {t.member.checkedIn}
                      </span>
                    )}
                    {ev.checkin_at && (
                      <button
                        data-testid={`member-certificate-btn-${ev.id}`}
                        onClick={() => downloadCert(ev)}
                        className="flex items-center gap-1.5 text-ochre hover:text-blaze transition-colors"
                      >
                        <Award size={12} />
                        {t.member.certificate}
                      </button>
                    )}
                  </div>
                  {!rigMeets(user.rig, ev.requirements) && (
                    <p data-testid={`member-rig-warning-${ev.id}`} className="mt-2.5 flex items-center gap-1.5 font-telemetry text-[10px] uppercase tracking-[0.15em] text-[#E86A50]">
                      <TriangleAlert size={12} />
                      {t.member.rigWarn}
                    </p>
                  )}
                  {new Date(ev.date) <= new Date() && !reviewedIds[ev.id] && (
                    reviewFor === ev.id ? (
                      <div className="mt-3 border-t border-line pt-3.5" data-testid={`review-form-${ev.id}`}>
                        <p className="font-telemetry text-[9px] uppercase tracking-[0.2em] text-blaze mb-2.5">
                          {t.member.reviewTitle}
                        </p>
                        <div className="flex gap-1 mb-2.5">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} data-testid={`review-star-${ev.id}-${n}`} onClick={() => setRating(n)}>
                              <Star size={18} className={n <= rating ? "text-ochre fill-ochre" : "text-line"} />
                            </button>
                          ))}
                        </div>
                        <textarea
                          rows={2}
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder={t.member.commentPh}
                          data-testid={`review-comment-${ev.id}`}
                          className="w-full bg-ink border border-line px-3 py-2 text-xs text-bone placeholder:text-faint focus:outline-none focus:border-blaze resize-none"
                        />
                        <div className="flex gap-3 mt-2.5">
                          <button
                            data-testid={`review-submit-${ev.id}`}
                            onClick={() => sendReview(ev)}
                            className="font-telemetry text-[10px] uppercase tracking-[0.18em] bg-blaze text-ink px-4 py-2 hover:bg-sand transition-colors"
                          >
                            {t.member.sendReview}
                          </button>
                          <button
                            onClick={() => setReviewFor(null)}
                            className="font-telemetry text-[10px] uppercase tracking-[0.18em] text-sandmut hover:text-blaze transition-colors"
                          >
                            {t.admin.cancel}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        data-testid={`review-open-${ev.id}`}
                        onClick={() => {
                          setReviewFor(ev.id);
                          setRating(5);
                          setComment("");
                        }}
                        className="mt-3 flex items-center gap-1.5 font-telemetry text-[10px] uppercase tracking-[0.18em] text-ochre hover:text-blaze transition-colors"
                      >
                        <Star size={12} />
                        {t.member.reviewCta}
                      </button>
                    )
                  )}
                  {reviewedIds[ev.id] && (
                    <p className="mt-3 flex items-center gap-1.5 font-telemetry text-[10px] uppercase tracking-[0.15em] text-[#7FB383]">
                      <CheckCircle2 size={12} />
                      {t.member.reviewed}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
